// The one place that knows how a profile's Currently Reading books are
// stored. Everything that shows them reads through currentlyReadingOf() (or
// firstCurrentlyReading() for a screen with room for one), and the Currently
// Reading block saves through currentlyReadingPatch().
//
// user_profiles.currently_reading is jsonb, and holds one of two shapes:
//   an object  one book, { book_id, title, author, cover_url, reaction,
//              source }. Every profile saved before the block took more than
//              one book looks like this.
//   an array   up to MAX_CURRENTLY_READING of those objects, in slide order.
//              Every save from the block writes this shape.
// Both are read the same way, so nothing has to be copied across and a
// reader's old book shows as slide 1 until they next save.
//
// No migration, on purpose. The column has no rule on its shape, and keeping
// the list in the same column means the two things that already read it
// keep seeing it: the cover cache, which walks objects and arrays alike
// (jsonb_replace_strings and jsonb_strings, 20260817050000_cover_cache.sql),
// and the author portal's Find Help directory, which gets the column as is
// from get_author_directory(). A new column would be invisible to both.
//
// The author portal reads the same column in its Find Help directory, and
// has its own copy of the read rule below: currentlyReadingBooks() in
// reanlo-author src/hooks/useAuthorDirectory.js. Change the shape here and
// that copy has to change with it.
//
// The limit of four is checked here and nowhere in the database. Nothing but
// the block writes this column, and a longer list would only be shown, never
// lost: currentlyReadingOf() does not cut it short.

// Matches MAX_HOT_TAKES (src/lib/hotTakes.js) on purpose: the two blocks
// work the same way, one at a time with arrows. Nothing in the database has
// to agree with this number.
export const MAX_CURRENTLY_READING = 4;

const isBook = b =>
  !!b && typeof b === 'object' && !Array.isArray(b)
  && typeof b.title === 'string' && b.title.trim() !== '';

// The books to show, in slide order. Never null.
export function currentlyReadingOf(profile) {
  const value = profile?.currently_reading;
  if (Array.isArray(value)) return value.filter(isBook);
  return isBook(value) ? [value] : [];
}

// Slide 1: what the share image and the Find Help directory show.
export function firstCurrentlyReading(profile) {
  return currentlyReadingOf(profile)[0] || null;
}

// The update to send for a list. An empty list is stored as NULL, which is
// what a profile that never set a book already has, so "nothing" has one
// spelling in the table.
export function currentlyReadingPatch(list) {
  const clean = (list || []).filter(isBook);
  return { currently_reading: clean.length ? clean : null };
}

const norm = s => (typeof s === 'string' ? s.trim().toLowerCase() : '');

// Whether two entries are the same book. A search result names its author
// author_name and a stored entry names it author, so both are accepted. The
// book id decides when both sides have one; otherwise title and author do.
export function isSameBook(a, b) {
  if (!a || !b) return false;
  const idA = a.book_id ?? a.id ?? null;
  const idB = b.book_id ?? b.id ?? null;
  if (idA && idB) return idA === idB;
  return norm(a.title) !== '' && norm(a.title) === norm(b.title)
    && norm(a.author ?? a.author_name) === norm(b.author ?? b.author_name);
}

// Whether two lists hold the same books, with the same reactions, in the same
// order. Key order is ignored on purpose: the column is jsonb, and Postgres
// returns an object's keys in its own order (shortest key first), so an entry
// read back from the database never stringifies the same as one written here.
// Undo compares with this, never with JSON.stringify.
export function sameCurrentlyReading(a, b) {
  const x = Array.isArray(a) ? a : [];
  const y = Array.isArray(b) ? b : [];
  return x.length === y.length
    && x.every((book, i) => isSameBook(book, y[i])
      && (book.reaction || '') === (y[i].reaction || ''));
}
